import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getAgreements, createAgreement, updateAgreement, uploadAgreementFile, getAgreementFileUrl } from '../../services/centerMembershipService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const statusColors = {
  active: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  terminated: 'bg-gray-100 text-gray-600',
  draft: 'bg-yellow-100 text-yellow-700',
};

const AgreementsPage = () => {
  const { userProfile, isSuperAdmin, getUserCenterIds } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [form, setForm] = useState({
    center_id: '',
    company_name: '',
    contact_person: '',
    billing_type: 'seat',
    booked_seats: '',
    booked_sqft: '',
    rent_amount: '',
    start_date: '',
    end_date: '',
    lock_in_months: 0,
    deposit_amount: 0,
    escalation_percent: 0,
    status: 'active',
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [agreementsData, centersData] = await Promise.all([
        getAgreements(),
        supabase?.from('centers')?.select('id, name, city')?.eq('is_active', true)
      ]);
      setAgreements(agreementsData || []);
      setCenters(centersData?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setUploading(true);
      const payload = {
        ...form,
        booked_seats: form?.billing_type === 'seat' ? parseInt(form?.booked_seats) || null : null,
        booked_sqft: form?.billing_type === 'sqft' ? parseFloat(form?.booked_sqft) || null : null,
        rent_amount: parseFloat(form?.rent_amount),
        lock_in_months: parseInt(form?.lock_in_months) || 0,
        deposit_amount: parseFloat(form?.deposit_amount) || 0,
        escalation_percent: parseFloat(form?.escalation_percent) || 0,
      };

      const { data: newAgreement, error: createErr } = await createAgreement(payload);
      if (createErr) throw createErr;

      // Upload file if selected
      if (selectedFile && newAgreement?.id) {
        const { filePath, error: uploadErr } = await uploadAgreementFile(selectedFile, newAgreement?.id);
        if (!uploadErr && filePath) {
          await updateAgreement(newAgreement?.id, { file_path: filePath, file_name: selectedFile?.name });
        }
      }

      setShowForm(false);
      setSelectedFile(null);
      setForm({
        center_id: '', company_name: '', contact_person: '', billing_type: 'seat',
        booked_seats: '', booked_sqft: '', rent_amount: '', start_date: '', end_date: '',
        lock_in_months: 0, deposit_amount: 0, escalation_percent: 0, status: 'active', notes: '',
      });
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to create agreement');
    } finally {
      setUploading(false);
    }
  };

  const handleViewFile = async (filePath) => {
    const { url, error } = await getAgreementFileUrl(filePath);
    if (url) window.open(url, '_blank');
    else alert('Could not load file: ' + error?.message);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Agreements</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage lease agreements and document uploads</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2">
          <Icon name="Plus" size={16} />
          New Agreement
        </Button>
      </div>
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm">
          {error}
        </div>
      )}
      {/* Create Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4">New Agreement</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Center *</label>
              <select
                value={form?.center_id}
                onChange={e => setForm(f => ({ ...f, center_id: e?.target?.value }))}
                required
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                <option value="">Select center</option>
                {centers?.map(c => <option key={c?.id} value={c?.id}>{c?.name} – {c?.city}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Company Name *</label>
              <input
                type="text"
                value={form?.company_name}
                onChange={e => setForm(f => ({ ...f, company_name: e?.target?.value }))}
                required
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                placeholder="Company name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Contact Person</label>
              <input
                type="text"
                value={form?.contact_person}
                onChange={e => setForm(f => ({ ...f, contact_person: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                placeholder="Contact name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Billing Type *</label>
              <select
                value={form?.billing_type}
                onChange={e => setForm(f => ({ ...f, billing_type: e?.target?.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              >
                <option value="seat">Seat</option>
                <option value="sqft">Sqft</option>
              </select>
            </div>

            {form?.billing_type === 'seat' ? (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Booked Seats *</label>
                <input
                  type="number"
                  value={form?.booked_seats}
                  onChange={e => setForm(f => ({ ...f, booked_seats: e?.target?.value }))}
                  required
                  min="1"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Booked Sqft *</label>
                <input
                  type="number"
                  value={form?.booked_sqft}
                  onChange={e => setForm(f => ({ ...f, booked_sqft: e?.target?.value }))}
                  required
                  min="1"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Monthly Rent (₹) *</label>
              <input
                type="number"
                value={form?.rent_amount}
                onChange={e => setForm(f => ({ ...f, rent_amount: e?.target?.value }))}
                required
                min="0"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Start Date *</label>
              <input
                type="date"
                value={form?.start_date}
                onChange={e => setForm(f => ({ ...f, start_date: e?.target?.value }))}
                required
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">End Date *</label>
              <input
                type="date"
                value={form?.end_date}
                onChange={e => setForm(f => ({ ...f, end_date: e?.target?.value }))}
                required
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Lock-in (months)</label>
              <input
                type="number"
                value={form?.lock_in_months}
                onChange={e => setForm(f => ({ ...f, lock_in_months: e?.target?.value }))}
                min="0"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Deposit (₹)</label>
              <input
                type="number"
                value={form?.deposit_amount}
                onChange={e => setForm(f => ({ ...f, deposit_amount: e?.target?.value }))}
                min="0"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Escalation (%)</label>
              <input
                type="number"
                value={form?.escalation_percent}
                onChange={e => setForm(f => ({ ...f, escalation_percent: e?.target?.value }))}
                min="0"
                max="100"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Upload Agreement Document (PDF/Image)</label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={e => setSelectedFile(e?.target?.files?.[0] || null)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
              />
              {selectedFile && <p className="text-xs text-muted-foreground mt-1">Selected: {selectedFile?.name}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
              <textarea
                value={form?.notes}
                onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
                rows={2}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground"
                placeholder="Additional notes..."
              />
            </div>

            <div className="md:col-span-2 flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={uploading}>
                {uploading ? <><Icon name="Loader2" size={14} className="animate-spin mr-1" />Saving...</> : 'Create Agreement'}
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* Agreements Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Company</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Center</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Billing</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Rent</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Period</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium">Doc</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {agreements?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Icon name="FileText" size={32} className="mx-auto mb-2 opacity-40" />
                    <p>No agreements yet</p>
                  </td>
                </tr>
              ) : (
                agreements?.map(agreement => (
                  <tr key={agreement?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{agreement?.company_name}</p>
                      <p className="text-xs text-muted-foreground">{agreement?.contact_person}</p>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {agreement?.centers?.name || agreement?.center_id}
                    </td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-foreground">{agreement?.billing_type}</span>
                      <p className="text-xs text-muted-foreground">
                        {agreement?.billing_type === 'seat'
                          ? `${agreement?.booked_seats} seats`
                          : `${agreement?.booked_sqft} sqft`}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-foreground font-medium">
                      ₹{Number(agreement?.rent_amount)?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      <p>{agreement?.start_date}</p>
                      <p>→ {agreement?.end_date}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors?.[agreement?.status] || 'bg-gray-100 text-gray-600'}`}>
                        {agreement?.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {agreement?.file_path ? (
                        <button
                          onClick={() => handleViewFile(agreement?.file_path)}
                          className="flex items-center gap-1 text-primary hover:underline text-xs"
                        >
                          <Icon name="FileDown" size={14} />
                          View
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">No file</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AgreementsPage;
